import React from 'react';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  assetsPricing,
  pbPreferredPricing,
} from '../../../../../constants/PricingBenefits';
import { HowItWorksModal, VideoModal } from './components/Modals';
import { Img } from './components/Shared';

interface MobileProps {
  isMobile: boolean;
}

interface PrefPricingBannerProps {
  isMobile: boolean;
  isQualified: boolean;
}

const constants = pbPreferredPricing.PrefPricingBanner;

const UnqualifiedDesktop = (): JSX.Element => {
  return (
    <div
      className="sui-grid sui-grid-cols-12 sui-bg-inactive"
      data-testid="UnqualifiedDesktop"
    >
      <div className="sui-col-span-7 xl:sui-col-span-6 sui-p-4 sui-flex">
        <div
          className="sui-pt-3 sui-pr-4"
          data-testid="UnqualifiedDesktop__Logo"
        >
          <Img src={assetsPricing.dollarLogo} />
        </div>
        <div
          /* eslint-disable-next-line tailwindcss/no-arbitrary-value */
          className="sui-max-w-[370px]"
          data-testid="UnqualifiedDesktop__Content"
        >
          <div className="lg:sui-hidden">
            <p className="sui-font-bold sui-text-3xl sui-pt-3">
              {constants.unqualified.title}
            </p>
            <p className="sui-text-base sui-pt-3 sui-pb-3 sui-pr-7">
              {constants.unqualified.content}
            </p>
          </div>
          <div className="sui-hidden lg:sui-block">
            <p className="sui-font-bold sui-text-5xl sui-pt-3">
              {constants.unqualified.title}
            </p>
            <p className="sui-text-lg sui-pt-3 sui-pb-3 sui-pr-7">
              {constants.unqualified.content}
            </p>
          </div>
        </div>
      </div>
      <div
        className="xl:sui-hidden sui-col-span-5 xl:sui-col-span-6 sui-flex sui-flex-col sui-justify-center sui-pr-3"
        data-testid="UnqualifiedDesktop__Video"
      >
        <VideoModal rounded isMobile={false} />
      </div>
      <div
        className="sui-hidden xl:sui-flex sui-col-span-5 xl:sui-col-span-6"
        data-testid="UnqualifiedDesktop__Video"
      >
        <VideoModal rounded isMobile={false} />
      </div>
    </div>
  );
};

const UnqualifiedMobile = (): JSX.Element => {
  const Title = () => (
    <p
      className="sui-font-bold sui-text-lg"
      data-testid="UnqualifiedMobile__Title"
      style={{ maxWidth: '270px' }}
    >
      {constants.unqualified.title}
    </p>
  );
  const Content = () => (
    <p
      className="sui-py-4 sui-text-base"
      data-testid="UnqualifiedMobile__Content"
    >
      {constants.unqualified.content}
    </p>
  );
  const Video = () => (
    <p data-testid="UnqualifiedMobile__Video">
      <VideoModal isMobile rounded />
    </p>
  );
  return (
    <div data-testid="UnqualifiedMobile">
      <Title />
      <Content />
      <Video />
    </div>
  );
};

const TierQualifiedDesktop = (): JSX.Element => {
  return (
    <div
      data-testid="TierQualifiedDesktop"
      className="sui-flex sui-bg-inactive sui-p-2"
    >
      <div className="sui-p-6" data-testid="TierQualifiedDesktop__Logo">
        <Img src={assetsPricing.dollarLogo} />
      </div>
      <div
        className="sui-p-6 sui-pl-0"
        data-testid="TierQualifiedDesktop__Content"
      >
        <div className="lg:sui-hidden">
          <p className="sui-text-3xl sui-pb-2 sui-font-bold">
            {constants.qualified.titleDesktop.line1}
          </p>
          <p className="sui-text-lg sui-pb-5">
            {constants.qualified.titleDesktop.line2}
          </p>
        </div>
        <div className="sui-hidden lg:sui-block">
          <p className="sui-text-5xl sui-pb-2 sui-font-bold">
            {constants.qualified.titleDesktop.line1}
          </p>
          <p className="sui-text-3xl sui-pb-5 sui-font-bold">
            {constants.qualified.titleDesktop.line2}
          </p>
        </div>
        <div className="sui-text-xl sui-font-bold">
          <HowItWorksModal isMobile={false} />
        </div>
      </div>
    </div>
  );
};

const TierQualifiedMobile = (): JSX.Element => {
  const Title = () => (
    <div className="sui-flex sui-mb-2" data-testid="TierQualifiedMobile__Title">
      <div className="sui-pr-4">
        <Img src={assetsPricing.dollarLogo} width="56" height="56" />
      </div>
      <div>
        <p className="sui-text-lg sui-font-bold">
          {constants.qualified.titleMobile.line1}
        </p>
        <Typography variant="h2" weight="display">
          {constants.qualified.titleMobile.line2}
        </Typography>
      </div>
    </div>
  );
  return (
    <div data-testid="TierQualifiedMobile">
      <Title />
      <HowItWorksModal isMobile />
    </div>
  );
};

const RenderUnqualified = ({ isMobile }: MobileProps): JSX.Element => {
  return isMobile ? <UnqualifiedMobile /> : <UnqualifiedDesktop />;
};

const RenderTierQualified = ({ isMobile }: MobileProps): JSX.Element => {
  return isMobile ? <TierQualifiedMobile /> : <TierQualifiedDesktop />;
};

export const PrefPricingBanner = (
  props: PrefPricingBannerProps,
): JSX.Element => {
  const { isMobile, isQualified } = props;
  const FinalRender = () =>
    isQualified ? (
      <RenderTierQualified isMobile={isMobile} />
    ) : (
      <RenderUnqualified isMobile={isMobile} />
    );
  return (
    <div className={isMobile ? '' : 'sui-pt-3'}>
      <FinalRender />
    </div>
  );
};
